export const isStruct = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export interface SessionStore<T> {
  subscribe(listener: () => void): () => void;
  getSnapshot(): T;
  getServerSnapshot(): T;
  read(): T;
  write(value: T): void;
  clear(): void;
}

export interface SessionStoreOptions<T> {
  key: string;
  empty: T;
  parse: (raw: string) => T;
  serialize: (value: T) => string;
}

const readRaw = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const createSessionStore = <T>({
  key,
  empty,
  parse,
  serialize,
}: SessionStoreOptions<T>): SessionStore<T> => {
  const listeners = new Set<() => void>();
  let snapshot: T = empty;
  let lastRaw: string | null = null;
  let listening = false;

  const sync = (): boolean => {
    const raw = readRaw(key);
    if (raw === lastRaw) {
      return false;
    }
    lastRaw = raw;
    snapshot = raw === null ? empty : parse(raw);
    return true;
  };

  const onStorageEvent = (event: StorageEvent): void => {
    if (event.storageArea && event.storageArea !== window.sessionStorage) {
      return;
    }
    if (event.key !== null && event.key !== key) {
      return;
    }
    if (sync()) {
      listeners.forEach(listener => listener());
    }
  };

  const ensureListening = (): void => {
    if (listening) {
      return;
    }
    listening = true;
    sync();
    window.addEventListener('storage', onStorageEvent);
  };

  const publish = (nextRaw: string | null): void => {
    const previousRaw = readRaw(key);
    if (previousRaw === nextRaw) {
      if (sync()) {
        listeners.forEach(listener => listener());
      }
      return;
    }

    try {
      if (nextRaw === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, nextRaw);
      }
    } catch {
      return;
    }

    window.dispatchEvent(
      new StorageEvent('storage', {
        key,
        oldValue: previousRaw,
        newValue: nextRaw,
        storageArea: window.sessionStorage,
      }),
    );
  };

  return {
    subscribe(listener: () => void) {
      ensureListening();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      ensureListening();
      return snapshot;
    },

    getServerSnapshot() {
      return empty;
    },

    read() {
      ensureListening();
      return snapshot;
    },

    write(value: T) {
      ensureListening();
      publish(serialize(value));
    },

    clear() {
      ensureListening();
      publish(null);
    },
  };
};
